import { useMemo, useState } from 'react';
import {
  useFetchReportsSummaryQuery,
  useFetchRevenueReportQuery,
  useFetchExpensesReportQuery,
  useFetchUtilityExpensesReportQuery,
} from '../../api';
import { formatAmount } from '../../utils';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const RevenueReport = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const range = useMemo(() => {
    const start = startDate ?? dayjs().startOf('month');
    const end = endDate ?? dayjs();
    return {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      from: start.startOf('day').toISOString(),
      to: end.endOf('day').toISOString(),
    };
  }, [startDate, endDate]);

  const { data: menuSummary, isFetching: menuFetching } = useFetchReportsSummaryQuery({
    from: range.from,
    to: range.to,
  });
  const { data: officesRevenue, isFetching: officesFetching } = useFetchRevenueReportQuery({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  const { data: expensesReport, isFetching: expensesFetching } = useFetchExpensesReportQuery({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  const { data: utilityReport, isFetching: utilityFetching } = useFetchUtilityExpensesReportQuery({
    startDate: range.startDate,
    endDate: range.endDate,
  });

  const isLoading = menuFetching || officesFetching || expensesFetching || utilityFetching;

  const menuRevenue = Number(menuSummary?.totalRevenue || 0);
  const membershipRevenue = Number(officesRevenue?.membershipRevenue || 0);
  const rentalRevenue = Number(officesRevenue?.rentalRevenue || 0);
  const officeRevenue = Number(officesRevenue?.officeRevenue || 0);
  const officesTotal = membershipRevenue + rentalRevenue + officeRevenue;

  const operatingExpenses = Number(expensesReport?.totalAmount || 0);
  const utilityExpenses = Number(utilityReport?.totalAmount || 0);

  const totalRevenue = menuRevenue + officesTotal;
  const totalExpenses = operatingExpenses + utilityExpenses;
  const net = totalRevenue - totalExpenses;
  const isProfit = net >= 0;

  const revenueBreakdown = [
    { label: 'Menu', value: menuRevenue, color: 'bg-emerald-500' },
    { label: 'Membership', value: membershipRevenue, color: 'bg-blue-500' },
    { label: 'Rental', value: rentalRevenue, color: 'bg-purple-500' },
    { label: 'Office', value: officeRevenue, color: 'bg-amber-500' },
  ];

  const mergedExpenseCategories: Record<string, number> = {};
  if (expensesReport?.byCategory) {
    for (const [k, v] of Object.entries(expensesReport.byCategory as Record<string, number>)) {
      mergedExpenseCategories[k] = (mergedExpenseCategories[k] || 0) + Number(v);
    }
  }
  if (utilityReport?.byCategory) {
    for (const [k, v] of Object.entries(utilityReport.byCategory as Record<string, number>)) {
      mergedExpenseCategories[k] = (mergedExpenseCategories[k] || 0) + Number(v);
    }
  }
  const expenseByCategory: [string, number][] = Object.entries(mergedExpenseCategories).sort((a, b) => b[1] - a[1]);

  const maxComparison = Math.max(totalRevenue, totalExpenses) || 1;

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 md:p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Typography variant="h6" className="font-bold text-slate-800">Revenue vs Expenses</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="flex flex-wrap items-center gap-3">
            <DatePicker label="Start Date" value={startDate} onChange={setStartDate} slotProps={{ textField: { size: 'small' } }} />
            <DatePicker label="End Date" value={endDate} onChange={setEndDate} slotProps={{ textField: { size: 'small' } }} />
          </div>
        </LocalizationProvider>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[200px] text-gray-500 italic">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NetTile
              label={isProfit ? 'Net Profit' : 'Net Loss'}
              value={`RWF ${formatAmount(Math.abs(net))}`}
              positive={isProfit}
            />
            <SummaryTile label="Total Revenue" value={`RWF ${formatAmount(totalRevenue)}`} accent="emerald" />
            <SummaryTile label="Total Expenses" value={`RWF ${formatAmount(totalExpenses)}`} accent="rose" />
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="font-bold text-slate-800 text-sm mb-4">Revenue vs Expenses</h3>
            <div className="space-y-4">
              <ComparisonBar label="Revenue" value={totalRevenue} max={maxComparison} color="bg-emerald-500" />
              <ComparisonBar label="Expenses" value={totalExpenses} max={maxComparison} color="bg-rose-500" />
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100 text-sm text-slate-600">
              {totalRevenue > 0 ? (
                <span>
                  Expense ratio:{' '}
                  <span className="font-bold text-slate-900">
                    {((totalExpenses / totalRevenue) * 100).toFixed(1)}%
                  </span>{' '}
                  of revenue
                </span>
              ) : (
                <span className="text-slate-400">No revenue in this period</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 text-sm mb-4">Revenue Sources</h3>
              {totalRevenue === 0 ? (
                <p className="text-slate-400 text-sm">No revenue recorded</p>
              ) : (
                <div className="space-y-3">
                  {revenueBreakdown.map((row) => {
                    const pct = totalRevenue > 0 ? (row.value / totalRevenue) * 100 : 0;
                    return (
                      <div key={row.label}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{row.label}</span>
                          <span className="text-sm font-bold text-slate-900">
                            RWF {formatAmount(row.value)}{' '}
                            <span className="text-xs text-slate-400 font-medium">({pct.toFixed(1)}%)</span>
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${row.color} rounded-full`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 text-sm mb-4">Expense Categories</h3>
              {expenseByCategory.length === 0 ? (
                <p className="text-slate-400 text-sm">No expenses recorded</p>
              ) : (
                <div className="space-y-3">
                  {expenseByCategory.map(([category, value]) => {
                    const pct = totalExpenses > 0 ? (value / totalExpenses) * 100 : 0;
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{category}</span>
                          <span className="text-sm font-bold text-slate-900">
                            RWF {formatAmount(value)}{' '}
                            <span className="text-xs text-slate-400 font-medium">({pct.toFixed(1)}%)</span>
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-rose-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
              <h3 className="font-bold text-slate-800 text-sm">Period Summary</h3>
            </div>
            <table className="w-full border-collapse text-sm">
              <tbody>
                <SummaryRow label="Menu revenue" value={menuRevenue} />
                <SummaryRow label="Membership revenue" value={membershipRevenue} />
                <SummaryRow label="Rental revenue" value={rentalRevenue} />
                <SummaryRow label="Office revenue" value={officeRevenue} />
                <tr className="bg-emerald-50 border-t border-slate-200">
                  <td className="py-3 px-5 font-bold text-slate-900">Total revenue</td>
                  <td className="py-3 px-5 text-right font-bold text-emerald-700">RWF {formatAmount(totalRevenue)}</td>
                </tr>
                <SummaryRow label="Operating expenses" value={operatingExpenses} />
                <SummaryRow label="Utility expenses" value={utilityExpenses} />
                <tr className="bg-rose-50 border-t border-slate-200">
                  <td className="py-3 px-5 font-bold text-slate-900">Total expenses</td>
                  <td className="py-3 px-5 text-right font-bold text-rose-700">- RWF {formatAmount(totalExpenses)}</td>
                </tr>
                <tr className={`${isProfit ? 'bg-emerald-100' : 'bg-rose-100'} border-t-2 border-slate-300`}>
                  <td className="py-4 px-5 font-bold text-slate-900 uppercase text-xs tracking-widest">
                    {isProfit ? 'Net Profit' : 'Net Loss'}
                  </td>
                  <td className={`py-4 px-5 text-right font-bold text-lg ${isProfit ? 'text-emerald-700' : 'text-rose-700'}`}>
                    RWF {formatAmount(Math.abs(net))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, accent }: { label: string; value: string; accent: 'emerald' | 'rose' }) => {
  const color = accent === 'emerald' ? 'text-emerald-600' : 'text-rose-600';
  return (
    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
};

const NetTile = ({ label, value, positive }: { label: string; value: string; positive: boolean }) => (
  <div className={`p-4 rounded-2xl border shadow-sm ${positive ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
    <div className="flex items-center justify-between mb-1">
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</p>
      {positive ? <TrendingUpIcon className="text-emerald-600" fontSize="small" /> : <TrendingDownIcon className="text-rose-600" fontSize="small" />}
    </div>
    <p className={`text-xl font-bold ${positive ? 'text-emerald-700' : 'text-rose-700'}`}>{value}</p>
  </div>
);

const ComparisonBar = ({ label, value, max, color }: { label: string; value: number; max: number; color: string }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-sm font-bold text-slate-900">RWF {formatAmount(value)}</span>
      </div>
      <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: number }) => (
  <tr className="border-b border-slate-100">
    <td className="py-3 px-5 text-slate-700">{label}</td>
    <td className="py-3 px-5 text-right text-slate-900 font-medium">RWF {formatAmount(value)}</td>
  </tr>
);

export default RevenueReport;
